/* Game: a position plus the history needed for ko, repetition, the ply cap and passes (D2, D3). */
import { clonePosition, coreAlive, countAlive, countLost, hasCore, positionFromLayout, positionKey } from './position.ts';
import type { Layout, Position } from './position.ts';
import { candidateMoves, sameMove } from './moves.ts';
import { applyMove } from './resolve.ts';
import { BLACK, DEFAULT_RULES, WHITE, other } from './types.ts';
import type { EndReason, Event, GameResult, Move, Rules } from './types.ts';
import { LAYOUT } from './layouts.ts';

export interface Game {
  pos: Position;
  rules: Rules;
  ply: number;
  /** position key after each ply; keys[0] is the start position */
  keys: string[];
  /** how often each position has occurred */
  counts: Map<string, number>;
  /** consecutive passes */
  passes: number;
  result: GameResult | null;
  /** what the last move did */
  events: Event[];
  /** the move list; null = pass */
  moves: (Move | null)[];
}

export function newGame(layout: Layout = LAYOUT, rules: Rules = DEFAULT_RULES): Game {
  return gameFromPosition(positionFromLayout(layout), rules);
}

export function gameFromPosition(pos: Position, rules: Rules = DEFAULT_RULES): Game {
  const key = positionKey(pos);
  return { pos, rules, ply: 0, keys: [key], counts: new Map([[key, 1]]), passes: 0, result: null, events: [], moves: [] };
}

/** The position a move may not recreate (D3, ko): the board as it stood before the opponent's last move. */
export function koKey(g: Game): string | null {
  return g.rules.ko && g.keys.length >= 2 ? g.keys[g.keys.length - 2] : null;
}

export interface LegalMove { move: Move; pos: Position; events: Event[] }

/** Every legal move together with its outcome (which is also the consequence preview). */
export function legalMoves(g: Game): LegalMove[] {
  const out: LegalMove[] = [];
  if (g.result) return out;
  const ko = koKey(g);
  for (const move of candidateMoves(g.pos)) {
    const { pos, events } = applyMove(g.pos, move);
    if (ko !== null && positionKey(pos) === ko) continue;
    out.push({ move, pos, events });
  }
  return out;
}

/** Material tiebreak: more pieces on the board wins, equal is a draw. */
export function materialResult(pos: Position, reason: EndReason): GameResult {
  const w = countAlive(pos, WHITE), b = countAlive(pos, BLACK);
  return { winner: w > b ? WHITE : b > w ? BLACK : null, reason };
}

/** Result decided by the position alone (D2): the enemy Core has left the board, or enough enemy pieces have. */
export function decisiveResult(pos: Position, rules: Rules): GameResult | null {
  const coreGone = (s: 0 | 1): boolean => rules.coreWin && hasCore(pos, s) && !coreAlive(pos, s);
  const lostOut = (s: 0 | 1): boolean => countLost(pos, s) >= rules.loseAtLost;
  const blackBeaten = coreGone(BLACK) || lostOut(BLACK), whiteBeaten = coreGone(WHITE) || lostOut(WHITE);
  if (!blackBeaten && !whiteBeaten) return null;
  if (blackBeaten && whiteBeaten) return materialResult(pos, coreGone(BLACK) || coreGone(WHITE) ? 'core' : 'material');
  const winner = blackBeaten ? WHITE : BLACK;
  return { winner, reason: coreGone(other(winner)) ? 'core' : 'material' };
}

function advance(g: Game, pos: Position, events: Event[], move: Move | null): Game {
  const key = positionKey(pos);
  const counts = new Map(g.counts);
  const n = (counts.get(key) ?? 0) + 1;
  counts.set(key, n);
  const ng: Game = {
    pos, rules: g.rules, ply: g.ply + 1, keys: [...g.keys, key], counts,
    passes: move ? 0 : g.passes + 1, result: null, events, moves: [...g.moves, move],
  };
  ng.result = decisiveResult(pos, g.rules)
    ?? (g.rules.threefold && n >= 3 ? materialResult(pos, 'threefold') : null)
    ?? (ng.passes >= 2 ? materialResult(pos, 'passes') : null)
    ?? (ng.ply >= g.rules.plyCap ? materialResult(pos, 'cap') : null);
  return ng;
}

/** Play a move. Validates legality including ko. Returns the new game; the old one is untouched. */
export function play(g: Game, move: Move): Game {
  if (g.result) throw new Error('the game is over');
  if (!candidateMoves(g.pos).some(m => sameMove(m, move))) throw new Error('illegal move');
  const { pos, events } = applyMove(g.pos, move);
  const ko = koKey(g);
  if (ko !== null && positionKey(pos) === ko) throw new Error('ko: a move may not recreate the board as it stood before the opponent\'s last move');
  return advance(g, pos, events, move);
}

/** Play a move whose outcome was already computed by legalMoves (skips re-validation). */
export function playLegal(g: Game, lm: LegalMove): Game {
  if (g.result) throw new Error('the game is over');
  return advance(g, lm.pos, lm.events, lm.move);
}

/** Pass, allowed only when there is no legal move. Two passes in a row end the game on material. */
export function pass(g: Game): Game {
  if (g.result) throw new Error('the game is over');
  if (legalMoves(g).length) throw new Error('a pass is only allowed when there is no legal move');
  const pos = clonePosition(g.pos);
  pos.toMove = other(pos.toMove);
  return advance(g, pos, [], null);
}
